import { ReflectionRoleEnum } from './reflection-role-enum'
import { ReflectionGenderEnum } from './reflection-gender-enum'
import { ReflectionStatusEnum } from './reflection-status-enum'
import { ReflectionIdcardTypeEnum } from './reflection-idcard-type-enum'
import { ReflectionPupilTypeEnum } from './reflection-pupil-type-enum'
import { Guardian } from './guardian'

export interface ReflectionInterface {
  /**
   * 关联学生 id(家长账户关联的学生 id)
   */
  pupilId: number
  /**
   * 角色
   */
  role: ReflectionRoleEnum
  /**
   * 性别
   */
  gender?: ReflectionGenderEnum | null
  /**
   * 状态
   */
  status: ReflectionStatusEnum
  /**
   * 证件类型
   */
  idcardType?: ReflectionIdcardTypeEnum | null
  /**
   * 家长类型
   */
  pupilType?: ReflectionPupilTypeEnum | null
  /**
   * 关联的非正式用户 id
   */
  informalReflectionId: number
  /**
   * 入校时间
   */
  admissionedAt?: string | null
  /**
   * 离校时间
   */
  archivedAt?: string | null
  /**
   * 关联家长
   */
  guardians?: Guardian[]
  /**
   * 是否已读某通知
   */
  isReadNotification?: boolean
  name: string
  /**
   * 账户
   */
  account: string
  /**
   * 学工号
   */
  usin?: string | null
  phone?: string | null
  email?: string | null
  /**
   * 个性头像
   */
  avatar?: string | null
  /**
   * 图片
   */
  photo?: string | null
  /**
   * 证件号
   */
  idcard?: string | null
  /**
   * 届别
   */
  graduate?: string | null
  /**
   * 所属科目
   */
  subject?: string | null
  /**
   * 是否禁用
   */
  disabled: boolean
  /**
   * 是否要求修改密码
   */
  passwordChangeNeeded?: boolean
  /**
   * 注册时间
   */
  registedAt?: string | null
  /**
   * 生日
   */
  birthday?: string | null
  /**
   * 存储自定义字段的值
   */
  fields?: object
  /**
   * 主键 ID
   */
  id: number
  /**
   * 学校 ID
   */
  schoolId: number
  /**
   * 创建时间
   */
  createdAt: string
  /**
   * 更新时间
   */
  updatedAt: string
}

export class Reflection implements ReflectionInterface {
  /**
   * 关联学生 id(家长账户关联的学生 id)
   */
  pupilId: number
  /**
   * 角色
   */
  role: ReflectionRoleEnum
  /**
   * 性别
   */
  gender?: ReflectionGenderEnum | null
  /**
   * 状态
   */
  status: ReflectionStatusEnum
  /**
   * 证件类型
   */
  idcardType?: ReflectionIdcardTypeEnum | null
  /**
   * 家长类型
   */
  pupilType?: ReflectionPupilTypeEnum | null
  /**
   * 关联的非正式用户 id
   */
  informalReflectionId: number
  /**
   * 入校时间
   */
  admissionedAt?: string | null
  /**
   * 离校时间
   */
  archivedAt?: string | null
  /**
   * 关联家长
   */
  guardians?: Guardian[]
  /**
   * 是否已读某通知
   */
  isReadNotification?: boolean
  name: string
  /**
   * 账户
   */
  account: string
  /**
   * 学工号
   */
  usin?: string | null
  phone?: string | null
  email?: string | null
  /**
   * 个性头像
   */
  avatar?: string | null
  /**
   * 图片
   */
  photo?: string | null
  /**
   * 证件号
   */
  idcard?: string | null
  /**
   * 届别
   */
  graduate?: string | null
  /**
   * 所属科目
   */
  subject?: string | null
  /**
   * 是否禁用
   */
  disabled: boolean
  /**
   * 是否要求修改密码
   */
  passwordChangeNeeded?: boolean
  /**
   * 注册时间
   */
  registedAt?: string | null
  /**
   * 生日
   */
  birthday?: string | null
  /**
   * 存储自定义字段的值
   */
  fields?: object
  /**
   * 主键 ID
   */
  id: number
  /**
   * 学校 ID
   */
  schoolId: number
  /**
   * 创建时间
   */
  createdAt: string
  /**
   * 更新时间
   */
  updatedAt: string

  constructor(props: ReflectionInterface) {
    const {
      // 重命名变量以规避与 JS 保留词的冲突, 比如 default, with
      pupilId: _pupilId,
      role: _role,
      gender: _gender,
      status: _status,
      idcardType: _idcardType,
      pupilType: _pupilType,
      informalReflectionId: _informalReflectionId,
      admissionedAt: _admissionedAt,
      archivedAt: _archivedAt,
      guardians: _guardians,
      isReadNotification: _isReadNotification,
      name: _name,
      account: _account,
      usin: _usin,
      phone: _phone,
      email: _email,
      avatar: _avatar,
      photo: _photo,
      idcard: _idcard,
      graduate: _graduate,
      subject: _subject,
      disabled: _disabled,
      passwordChangeNeeded: _passwordChangeNeeded,
      registedAt: _registedAt,
      birthday: _birthday,
      fields: _fields,
      id: _id,
      schoolId: _schoolId,
      createdAt: _createdAt,
      updatedAt: _updatedAt,
      // 可能会有额外字段比如自定义字段不在类型定义中
      ...rest
    } = props

    Object.assign(this, rest)

    this.pupilId = _pupilId
    this.role = _role
    this.gender = _gender
    this.status = _status
    this.idcardType = _idcardType
    this.pupilType = _pupilType
    this.informalReflectionId = _informalReflectionId
    this.admissionedAt = _admissionedAt
    this.archivedAt = _archivedAt
    this.guardians = _guardians
      ? _guardians.map((i) => new Guardian(i))
      : _guardians
    this.isReadNotification = _isReadNotification
    this.name = _name
    this.account = _account
    this.usin = _usin
    this.phone = _phone
    this.email = _email
    this.avatar = _avatar
    this.photo = _photo
    this.idcard = _idcard
    this.graduate = _graduate
    this.subject = _subject
    this.disabled = _disabled
    this.passwordChangeNeeded = _passwordChangeNeeded
    this.registedAt = _registedAt
    this.birthday = _birthday
    this.fields = _fields
    this.id = _id
    this.schoolId = _schoolId
    this.createdAt = _createdAt
    this.updatedAt = _updatedAt
  }
}
